import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Creston = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.creston-bc:About Creston Paragraph 1">
            Creston, nestled in the scenic Kootenay region of southeastern{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            , is a charming town known for its agricultural abundance and
            close-knit community. Framed by the Selkirk and Purcell mountains,
            Creston’s fertile lands foster a thriving farming sector with
            bountiful orchards, embodying the agricultural essence found in{' '}
            <LinkText href={BcNeighbourhoodsLink}>
              cities and neighbourhoods across BC
            </LinkText>
            . The town’s fruit production, particularly apples and cherries,
            contributes to vibrant farmers’ markets and a farm-to-table culture.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.creston-bc:About Creston Paragraph 2">
            Beyond its agrarian heritage, Creston offers recreational activities
            like hiking, fishing, and birdwatching, while its arts scene,
            Japanese-Canadian history, wineries, and breweries add cultural
            depth. As the &quot;Gateway to the Kootenays,&quot; Creston
            exemplifies the harmony between nature’s gifts and community
            vibrancy, characterizing the heart of BC’s diverse locales.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$900 - $1,900',
          bed2: '$1,300 - $2,300',
          bed3: '$2,300 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.creston-bc:Apartment Hunting in Creston Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Creston</LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Apartments for rent Creston Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Creston
              </LinkText>{' '}
              are often appealing to singles, couples, or small families. These
              units provide a practical and convenient living option, especially
              for those who value proximity to local amenities like shops,
              cafes, and community services. Apartments in Creston range from
              older, more affordable buildings to newer complexes, and they
              typically offer a maintenance-free lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Houses for rent Creston Paragraph">
              <LinkText href={links.house}>Houses for rent in Creston</LinkText>{' '}
              are popular among families or those who desire more space and
              privacy. These homes often come with yards, providing room for
              outdoor activities and gardening. The variety in Creston includes
              charming, older homes with character as well as more modern
              constructions, offering options for different tastes and needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Townhouses for rent Creston Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Creston
              </LinkText>{' '}
              offer a balance of community living and private space. They are a
              preferred choice for small families, couples, or professionals.
              These units often feature multiple levels and sometimes small
              private yards or patios, and they are usually part of smaller
              complexes, providing a sense of neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Basements for rent Creston Paragraph">
              Basements for rent in Creston provide an affordable housing
              choice, particularly suited to students, singles, or those on a
              tighter budget. Basements for rent are typically part of a larger
              house, offering privacy and basic amenities at a lower cost,
              though they may have limitations in terms of space and natural
              light.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Condos for rent Creston Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Creston
              </LinkText>{' '}
              are geared towards those seeking a hassle-free lifestyle. Condos
              here might include additional amenities like fitness centers or
              shared social spaces and are particularly attractive to young
              professionals, downsizers, or small families. They offer the
              convenience of apartment living with the added benefit of
              ownership-style features.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Rooms for rent Creston Paragraph">
              <LinkText href={links.room}>Renting a room in Creston</LinkText>{' '}
              is the most economical option, often chosen by students, seasonal
              workers, or individuals in need of flexible living arrangements.
              Rooms for rent typically involves shared common areas like
              kitchens and living rooms and can provide a social and communal
              living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Short term rentals Creston Paragraph">
              Creston, known for its agricultural industry and scenic beauty,
              offers a range of employment opportunities, though it may not be
              widely recognized for its academic institutions. While it might
              attract some interest in{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              this trend is not as prominent as in larger cities or towns with
              larger universities or colleges. The popularity of short-term
              rentals typically increases in many urban areas during summer
              months and at the start of school semesters, but this pattern can
              vary in smaller towns like Creston. Nonetheless, the flexibility
              that short-term rentals provide is an important consideration for
              renters, influencing their decision on whether{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                short-term rentals are suitable for their specific needs
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.creston-bc:Cheap rentals Creston Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Creston
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Creston, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.creston-bc:Living in Creston Paragraph">
            Living in Creston, British Columbia, offers a similar blend of
            scenic beauty and small-town charm. Situated in the southern part of
            the province, Creston is enveloped by breathtaking landscapes,
            characterized by majestic mountains, crystal-clear lakes, and
            verdant forests. The community in Creston is renowned for its warm
            and inviting ambiance, offering a plethora of amenities for both
            residents and tourists. Whether you’re seeking thrilling outdoor
            escapades such as hiking and skiing or prefer immersing yourself in
            cultural pursuits at local museums and art galleries, Creston caters
            to a diverse range of interests. With its pristine and secure
            setting, Creston provides an idyllic and tranquil living experience,
            ideal for those in pursuit of serenity.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.creston-bc:Living in Creston Paragraph 2">
            If you’re not sure that Creston is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.creston-bc:Popular Neighbourhoods Creston Paragraph">
          Like any city, Creston is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Creston, South Creston, Creston East, Creston West, Creston
          Flats, and Other Surrounding Areas. For more info about Creston, you
          can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.creston-bc:Local Landmarks Creston Paragraph">
          Creston, BC, is a charming town that boasts an array of captivating
          landmarks that showcase the area’s natural beauty and cultural
          heritage. One such landmark is the Creston Valley Wildlife Management
          Area, a sprawling sanctuary that offers a haven for wildlife
          enthusiasts and birdwatchers. The Creston Museum is another notable
          landmark, housing captivating exhibits that delve into the town’s rich
          history and heritage. For those seeking outdoor adventure, the Creston
          Mountain Trail provides breathtaking views of the surrounding
          mountains and valleys. Additionally, the Skimmerhorn Winery offers a
          taste of Creston’s flourishing wine industry. These landmarks
          contribute to the town’s unique charm and make Creston an enticing
          destination for visitors.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Creston
